import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  getAvailableFiltersProductsFiltersGet,
} from "@/client/sdk.gen";
import type { FilterCount, FiltersResponse } from "@/client/types.gen";
import "./Filter.css";

export interface FilterParams {
  totalProducts: number;
}

export default function Filter(params: FilterParams) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<FiltersResponse>({
    brands: [],
    categories: [],
    sites: [],
    sizes: [],
    conditions: [],
  });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    brands: false,
    categories: false,
    sites: false,
    availability: false,
  });

  const getFiltersFromURL = () => {
    const brandsParam = searchParams.get('brands');
    const categoriesParam = searchParams.get('categories');
    const sitesParam = searchParams.get('sites');
    const sizesParam = searchParams.get('sizes');
    const conditionsParam = searchParams.get('conditions');
    const in_stockParam = searchParams.get('in_stock');
    const min_priceParam = searchParams.get('min_price');
    const max_priceParam = searchParams.get('max_price');
    const days_since_updateParam = searchParams.get('days_since_update');
    const searchParam = searchParams.get('search');

    return {
      brands: brandsParam ? brandsParam.split(',').map(Number) : [],
      categories: categoriesParam ? categoriesParam.split(',').map(Number) : [],
      sites: sitesParam ? sitesParam.split(',').map(Number) : [],
      sizes: sizesParam ? sizesParam.split(',') : [],
      conditions: conditionsParam ? conditionsParam.split(',') : [],
      in_stock: in_stockParam === 'true' ? true : in_stockParam === 'false' ? false : undefined,
      min_price: min_priceParam ? Number(min_priceParam) : undefined,
      max_price: max_priceParam ? Number(max_priceParam) : undefined,
      days_since_update: days_since_updateParam ? Number(days_since_updateParam) : undefined,
      search: searchParam,
    };
  };

  const currentFilters = getFiltersFromURL();

  useEffect(() => {
    const params = getFiltersFromURL();
    setLoading(true);

    const apiParams = {
      brand_ids: params.brands.length ? params.brands.join(',') : undefined,
      category_ids: params.categories.length ? params.categories.join(',') : undefined,
      site_ids: params.sites.length ? params.sites.join(',') : undefined,
      sizes: params.sizes.length ? params.sizes.join(',') : undefined,
      condition: params.conditions.length ? params.conditions.join(',') : undefined,
      in_stock: params.in_stock,
      min_price: params.min_price,
      max_price: params.max_price,
      days_since_update: params.days_since_update,
      search: params.search,
    };

    getAvailableFiltersProductsFiltersGet({ query: apiParams })
      .then(response => {
        if (response.data) {
          setFilters(response.data);
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [searchParams]);

  const toggleSection = (section: string) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section],
    });
  };

  const updateURLParams = (newFilters: ReturnType<typeof getFiltersFromURL>, search?: string) => {
    const params = new URLSearchParams(searchParams);

    if (newFilters.brands.length > 0) {
      params.set('brands', newFilters.brands.join(','));
    } else {
      params.delete('brands');
    }

    if (newFilters.categories.length > 0) {
      params.set('categories', newFilters.categories.join(','));
    } else {
      params.delete('categories');
    }

    if (newFilters.sites.length > 0) {
      params.set('sites', newFilters.sites.join(','));
    } else {
      params.delete('sites');
    }

    if (newFilters.in_stock !== undefined) {
      params.set('in_stock', String(newFilters.in_stock));
    } else {
      params.delete('in_stock');
    }

    if (search !== undefined) {
      if (search.trim()) {
        params.set('search', search.trim());
      } else {
        params.delete('search');
      }
    }

    params.set('page', '1');

    setSearchParams(params, { replace: true });
  };

  const toggleFilter = (type: 'brands' | 'categories' | 'sites', id: number) => {
    const filters = getFiltersFromURL();
    const targetIdx = filters[type].indexOf(id);
    const arr = [...filters[type]];

    if (targetIdx === -1) {
      arr.push(id);
    } else {
      arr.splice(targetIdx, 1);
    }

    const newFilters = {
      ...filters,
      [type]: arr
    };

    updateURLParams(newFilters);
  };

  const handleAvailabilityChange = (option: string) => {
    const filters = getFiltersFromURL();
    const newFilters = {
      ...filters,
      in_stock: option === "available" ? true : option === "unavailable" ? false : undefined
    };
    updateURLParams(newFilters);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateURLParams(currentFilters, searchQuery);
  };

  const handleSearchClear = () => {
    setSearchQuery('');
    updateURLParams(currentFilters, '');
  };

  const handleSectionSearch = (section: string, value: string) => {
    setSectionSearches({
      ...sectionSearches,
      [section]: value
    })
  }

  const clearAllFilters = () => {
    setSearchQuery('');
    setSectionSearches({
      brands: '',
      categories: '',
      sites: '',
    })
    setSearchParams({}, { replace: true });
  };

  const hasActiveFilters = currentFilters.brands.length > 0 ||
    currentFilters.categories.length > 0 ||
    currentFilters.sites.length > 0 ||
    currentFilters.in_stock !== undefined ||
    currentFilters.search;

  const [sectionSearches, setSectionSearches] = useState<Record<string, string>>({
    brands: '',
    categories: '',
    sites: '',
  })

  const filterBySearch = (items: FilterCount[], searchTerm: string) => {
    if (!searchTerm.trim()) return items;

    const lowerSearch = searchTerm.toLowerCase();

    return items.filter(item => {
      const name = getItemName(item).toLowerCase();
      return name.includes(lowerSearch);
    })
  }

  if (loading) {
    return (
      <div className="filter-container">
        <div className="filter-loading">
          <div className="loading-spinner"></div>
          <span>Loading filters...</span>
        </div>
      </div>
    );
  }

  const getItemName = (item: FilterCount): string =>
    item.display_name ?? item.name ?? "";

  const sortItems = <T extends { id: number }>(
    items: T[],
    selectedIds: number[]
  ): T[] => {
    return [...items].sort((a, b) => {
      const aSelected = selectedIds.includes(a.id);
      const bSelected = selectedIds.includes(b.id);

      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;

      const aName = getItemName(a as any);
      const bName = getItemName(b as any);
      return aName.localeCompare(bName);
    });
  };

  const renderFilterSection = (
    title: string,
    sectionKey: "brands" | "categories" | "sites",
    items: FilterCount[]
  ) => {
    const isExpanded = expandedSections[sectionKey];
    const sortedItems = sortItems(items, currentFilters[sectionKey]);
    const searchTerm = sectionSearches[sectionKey] ?? '';
    const filteredItems = filterBySearch(sortedItems, searchTerm);
    const activeCount = currentFilters[sectionKey].length;

    return (
      <div key={sectionKey} className="filter-section">
        <button
          type="button"
          className="filter-section-header"
          onClick={() => toggleSection(sectionKey)}
        >
          <span className="filter-section-title">
            {title}
            {activeCount > 0 && <span className="filter-count-badge">{activeCount}</span>}
          </span>
          <span className="filter-arrow">{isExpanded ? "▼" : "▶"}</span>
        </button>
        {isExpanded && (
          <div className="filter-items">
            <div className="section-search-wrapper">
              <input
                type="text"
                value={sectionSearches[sectionKey] || ''}
                onChange={(e) => handleSectionSearch(sectionKey, e.target.value)}
                placeholder={`Search ${title.toLowerCase()}...`}
              />
            </div>
            {filteredItems.map((item) => {
              const isChecked = currentFilters[sectionKey].includes(item.id);
              return (
                <label key={item.id} className={`filter-item ${isChecked ? 'checked' : ''}`}>
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggleFilter(sectionKey, item.id)}
                  />
                  <span className="filter-checkbox"></span>
                  <span className="filter-label">{getItemName(item)}</span>
                  {item.count !== undefined && (
                    <span className="filter-item-count">({item.count})</span>
                  )}
                </label>
              );
            })}
            {filteredItems.length === 0 && (
              <div className="no-results">No results found</div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderAvailabilitySection = () => {
    const isExpanded = expandedSections.availability;
    const hasActiveFilter = currentFilters.in_stock !== undefined;

    return (
      <div className="filter-section">
        <button
          type="button"
          className="filter-section-header"
          onClick={() => toggleSection('availability')}
        >
          <span className="filter-section-title">
            Availability
            {hasActiveFilter && <span className="filter-count-badge">1</span>}
          </span>
          <span className="filter-arrow">{isExpanded ? "▼" : "▶"}</span>
        </button>
        {isExpanded && (
          <div className="filter-items">
            <label className={`filter-item ${currentFilters.in_stock === undefined ? 'checked' : ''}`}>
              <input
                type="radio"
                name="availability"
                checked={currentFilters.in_stock === undefined}
                onChange={() => handleAvailabilityChange("all")}
              />
              <span className="filter-checkbox"></span>
              <span className="filter-label">All Items</span>
            </label>

            <label className={`filter-item ${currentFilters.in_stock === true ? 'checked' : ''}`}>
              <input
                type="radio"
                name="availability"
                checked={currentFilters.in_stock === true}
                onChange={() => handleAvailabilityChange("available")}
              />
              <span className="filter-checkbox"></span>
              <span className="filter-label">Available</span>
            </label>

            <label className={`filter-item ${currentFilters.in_stock === false ? 'checked' : ''}`}>
              <input
                type="radio"
                name="availability"
                checked={currentFilters.in_stock === false}
                onChange={() => handleAvailabilityChange("unavailable")}
              />
              <span className="filter-checkbox"></span>
              <span className="filter-label">Unavailable</span>
            </label>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="filter-container">
      <div className="filter-header">
        <div className="products-total">
          Total Items - {params.totalProducts.toLocaleString()}
        </div>
        {hasActiveFilters && (
          <button className="clear-all-button" onClick={clearAllFilters}>
            Clear All
          </button>
        )}
      </div>

      <div className="filter-section search-section">
        <form onSubmit={handleSearchSubmit} className="search-form">
          <div className="search-input-wrapper">
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="Search products..."
              className="search-input"
            />
          </div>
        </form>
      </div>

      {renderFilterSection("Brands", "brands", filters.brands)}
      {renderFilterSection("Categories", "categories", filters.categories)}
      {renderFilterSection("Sites", "sites", filters.sites)}
      {renderAvailabilitySection()}
    </div>
  );
}